/**
 * Constantes estáticas do módulo de Carga de Dados.
 *
 * Dicionários compartilhados entre Carga.vue (que também os repassa por prop
 * ao Edit.vue), Rotina.vue e UltimaRotina.vue — antes duplicados em data() de
 * cada componente. Congelados: são dados de exibição/configuração imutáveis.
 */

// Rótulos humanos para as expressões cron aceitas pelas cargas/rotinas.
export const CRONS = Object.freeze({
  '* * * * *': 'A cada minuto',
  '*/2 * * * *': 'A cada 2 minutos',
  '*/3 * * * *': 'A cada 3 minutos',
  '*/4 * * * *': 'A cada 4 minutos',
  '*/5 * * * *': 'A cada 5 minutos',
  '*/6 * * * *': 'A cada 6 minutos',
  '*/7 * * * *': 'A cada 7 minutos',
  '*/8 * * * *': 'A cada 8 minutos',
  '*/9 * * * *': 'A cada 9 minutos',
  '*/10 * * * *': 'A cada 10 minutos',
  '*/15 * * * *': 'A cada 15 minutos',
  '*/20 * * * *': 'A cada 20 minutos',
  '*/30 * * * *': 'A cada 30 minutos',
  '0 */1 * * *': 'A cada 1 hora',
  '0 */2 * * *': 'A cada 2 horas',
  '0 */3 * * *': 'A cada 3 horas',
  '0 */4 * * *': 'A cada 4 horas',
  '0 */6 * * *': 'A cada 6 horas',
  '0 */8 * * *': 'A cada 8 horas',
  '0 */12 * * *': 'A cada 12 horas',
  '0 0 * * *': 'Pontualmente à 0h',
  '0 1 * * *': 'Pontualmente à 1h',
  '0 2 * * *': 'Pontualmente às 2h',
  '0 3 * * *': 'Pontualmente às 3h',
  '0 4 * * *': 'Pontualmente às 4h',
  '0 5 * * *': 'Pontualmente às 5h',
  '0 6 * * *': 'Pontualmente às 6h',
  '0 7 * * *': 'Pontualmente às 7h',
  '0 8 * * *': 'Pontualmente às 8h',
  '0 9 * * *': 'Pontualmente às 9h',
  '0 10 * * *': 'Pontualmente às 10h',
  '0 11 * * *': 'Pontualmente às 11h',
  '0 12 * * *': 'Pontualmente às 12h',
  '0 13 * * *': 'Pontualmente às 13h',
  '0 14 * * *': 'Pontualmente às 14h',
  '0 15 * * *': 'Pontualmente às 15h',
  '0 16 * * *': 'Pontualmente às 16h',
  '0 17 * * *': 'Pontualmente às 17h',
  '0 18 * * *': 'Pontualmente às 18h',
  '0 19 * * *': 'Pontualmente às 19h',
  '0 20 * * *': 'Pontualmente às 20h',
  '0 21 * * *': 'Pontualmente às 21h',
  '0 22 * * *': 'Pontualmente às 22h',
  '0 23 * * *': 'Pontualmente às 23h',
  '0 4,16 * * *': '2x do dia (4h e 16h)',
  '0 5,17 * * *': '2x do dia (5h e 17h)',
});

// Modos de sincronização das cargas: metadados de exibição e de comportamento
// do formulário de edição (quais campos cada modo usa).
export const MODOS = Object.freeze({
  'NCR': Object.freeze({
    alias: 'NCR',
    label: 'NCR (Padrão)',
    obs: '(NCR = Número de Controle de Registro) Formato padrão de sincronização padrão. Este formato obtém todos os' +
      ' registros do banco de dados do sistema de gestão e insere/atualiza os registros no banco de dados local.' +
      ' Os registros atualizados são "incrementados" e controlados por uma segunda tabela, que define ' +
      '  quais registros estão ativos',
    usaManutencao: true,
    usaModoExecucao: true,
    usaEliminaRegistros: true,
    usaMargemMinimaRegistros: true,
  }),
  'NCR_LOTE': Object.freeze({
    alias: 'LOTE',
    label: 'NCR EM LOTE (Recomendado para tabelas/views grandes)',
    obs: '(NCR = Número de Controle de Registro). Este formato obtém todos os' +
      ' registros do banco de dados do sistema de gestão em partes, utilizando consultas por LIMIT e OFFSET' +
      ' (dependendo do banco de dados pode utilizar consultas por ROWNUM), e insere/atualiza os registros no banco' +
      ' de dados local. Os registros atualizados são "incrementados" e controlados por uma segunda tabela,' +
      ' que define quais registros estão ativos',
    usaManutencao: true,
    usaModoExecucao: true,
    usaEliminaRegistros: true,
    usaMargemMinimaRegistros: false,
  }),
  'NCR_LOTE_INDEX': Object.freeze({
    alias: 'LOTE INDEXADO',
    label: 'NCR EM LOTE INDEXADO (Recomendado para tabelas/views grandes com coluna de indice)',
    obs: '(NCR = Número de Controle de Registro). Este formato obtém todos os' +
      ' registros do banco de dados do sistema de gestão em partes, utilizando uma coluna de indice' +
      ' para obter essas partes, e insere/atualiza os registros no banco' +
      ' de dados local. Os registros atualizados são "incrementados" e controlados por uma segunda tabela,' +
      ' que define quais registros estão ativos',
    usaManutencao: true,
    usaModoExecucao: true,
    usaEliminaRegistros: false,
    usaMargemMinimaRegistros: false,
  }),
  'COMPARATOR': Object.freeze({
    alias: 'COMPARADOR',
    label: 'COMPARADOR (Recomendado para tabelas/views pequenas/médias)',
    obs: 'Este formato obtém todos os registros do banco de dados do sistema de gestão e todos os registros' +
      ' do banco de dados local, após isso, faz a comparação dos dados entre as duas plataformas.' +
      ' Registros diferentes é resultam em UPDATE no banco local, registros que faltam localmente é feito o INSERT,' +
      ' e registros que não vierem do banco de gestão é feito o DELETE no local',
    usaManutencao: true,
    usaModoExecucao: true,
    usaEliminaRegistros: true,
    usaMargemMinimaRegistros: true,
  }),
  'HASH_COMPARATOR': Object.freeze({
    alias: 'HASH_COMPARATOR',
    label: 'HASH_COMPARATOR (Recomendado para tabelas/views pequenas/médias)',
    obs: 'Para utilizar a carga neste modo, é imprescindível que o valor da coluna ID seja ÚNICO. Caso contrário, a carga estará sujeita a problemas de sincronização. Este formato obtém todos os registros do banco de dados do sistema de gestão e todos os registros' +
      ' do banco de dados local, após isso, faz a comparação da coluna de HASH entre o local e o remoto.' +
      ' Registros diferentes resultam em UPDATE no banco local, registros que faltam localmente é feito o INSERT,' +
      ' e registros que não vierem do banco de gestão é feito o DELETE no local',
    usaManutencao: true,
    usaModoExecucao: true,
    usaEliminaRegistros: true,
    usaMargemMinimaRegistros: true,
  }),
  'HASH_LOTE': Object.freeze({
    alias: 'HASH_LOTE',
    label: 'HASH_LOTE (Recomendado para tabelas/views pequenas/médias/grandes)',
    obs: 'Para utilizar a carga neste modo, é imprescindível que o valor da coluna ID seja ÚNICO. Caso contrário, a carga estará sujeita a problemas de sincronização. Este formato obtém os registros do banco de dados do sistema de gestão de forma incremental e todos os registros' +
      ' do banco de dados local, após isso, faz a comparação da coluna de HASH entre o local e o remoto.' +
      ' Registros diferentes resultam em UPDATE no banco local, registros que faltam localmente é feito o INSERT,' +
      ' e registros que não vierem do banco de gestão é feito o DELETE no local',
    usaManutencao: true,
    usaModoExecucao: true,
    usaEliminaRegistros: true,
    usaMargemMinimaRegistros: true,
  }),
  'REFRESH_VIEW': Object.freeze({
    alias: 'REFRESH_VIEW',
    label: 'ATUALIZAÇÃO VIEW (Recomendado apenas para views locais materializadas)',
    obs: 'Este formato roda um processo localmente para efetuar a "materialização" dos registros em uma' +
      ' segunda view no banco de dados local, geralmente para aumentar a performance de consultas grandes',
    usaManutencao: false,
    usaModoExecucao: false,
    usaEliminaRegistros: false,
    usaMargemMinimaRegistros: false,
  }),
  'MONGO_CACHE': Object.freeze({
    alias: 'CACHE NOSQL',
    label: 'CACHE NOSQL (Recomendado apenas para tarefas locais)',
    obs: 'Este formato roda um processo de cacheamento no banco de dados do mongoDB.' +
      ' Nem todas as views e processos podem ser configurados para este modo, pois exije' +
      ' que programação seja efetuada para tal configuração.',
    usaManutencao: false,
    usaModoExecucao: false,
    usaEliminaRegistros: false,
    usaMargemMinimaRegistros: false,
  }),
  'JOB': Object.freeze({
    alias: 'JOB/SCRIPT',
    label: 'JOB (Executar script ou tarefa específica)',
    obs: 'Este não é um processo de sincronização, mas um processo pré-definido e programado para executar' +
      ' um conjunto de ações ou um script pré-definido. Nem todos os processo podem ser configurados para' +
      ' este formato, pois ele exije programação.',
    usaManutencao: false,
    usaModoExecucao: false,
    usaEliminaRegistros: false,
    usaMargemMinimaRegistros: true,
  }),
  'INCREMENTAL': Object.freeze({
    alias: 'INCREMENTAL',
    label: 'INCREMENTAL (Sincroniza apenas registros que sofreram atualização no ERP)',
    obs: 'Este formato obtém todos os registros que sofreram alterações desde sua última execução.' +
      ' Para rodar este tipo de consulta, é necessário que a view/tabela possua um campo chamado "dataatualizacao"' +
      ' que contenha a data e hora da última atualização do registro, após a sincronização, o sistema guarda' +
      ' a maior data/hora dentre os registros para utilizar na próxima sincronização. Obs: Para invalidar' +
      ' registros utilizando este formato, é necessário que o registro, mesmo que inativo, esteja disponível' +
      ' na view com o o campo "ativo" igual a "0". Para ativação deste formato, é necessário a criação de triggers' +
      'em cima das tabelas envolvidas no processo',
    usaManutencao: true,
    usaModoExecucao: true,
    usaEliminaRegistros: false,
    usaMargemMinimaRegistros: false,
  }),
  'PHP_SCRIPT': Object.freeze({
    alias: 'PHP/SCRIPT',
    label: 'PHP (Executar script em PHP)',
    obs: 'Este não é um processo de sincronização, mas um processo pré-definido e programado para executar' +
      ' um script em PHP. Nem todos os processo podem ser configurados para este formato, pois ele' +
      ' exije programação, em casos de dúvidas ou necessidade no uso, entrar em contato' +
      ' com um consultor comercial.',
    usaManutencao: false,
    usaModoExecucao: false,
    usaEliminaRegistros: false,
    usaMargemMinimaRegistros: false,
    tipoScript: 'text/x-php',
  }),
  'SQL_SCRIPT': Object.freeze({
    alias: 'SQL/SCRIPT',
    label: 'SQL (Executar script em SQL)',
    obs: 'Este não é um processo de sincronização, mas um processo pré-definido e programado para executar' +
      ' um script em SQL. Nem todos os processo podem ser configurados para este formato, pois ele' +
      ' exije programação, em casos de dúvidas ou necessidade no uso, entrar em contato' +
      ' com um consultor comercial.',
    usaManutencao: false,
    usaModoExecucao: false,
    usaEliminaRegistros: false,
    usaMargemMinimaRegistros: false,
    usaBancoExecucao: true,
    tipoScript: 'text/x-sql',
  }),
});
